import * as React from 'react';
import { useEffect, useContext } from 'react';
import { LoadingContext } from '../contexts/LoadingContextProvider';
import * as TFF from '@tff/types/tff';
import { keycloak } from '../keycloak';
import _ from 'lodash';
import { apigeeEndpoint } from '../apis';
import { UpdateTaskRequest } from '@tff/types/tff/ndc-task-service';

export type TypeOfAtcomResTaskStatus = 'UPDATING' | 'UPDATED' | 'FAILURE';

export type AtcomResTasksResponse = {
  fetchedAtcomResTaskList?: TFF.InvoluntaryTask[];
  fetchedAtcomResTaskStatus?: TypeOfAtcomResTaskStatus;
  changeStateOfSelected: (
    decision: TFF.TaskDecision,
    status: TFF.TaskStatus,
    selectedIDs: Partial<TFF.InvoluntaryTask>[],
  ) => void;
};

export const useAtcomResTasks = (): AtcomResTasksResponse => {
  const [fetchedAtcomResTaskStatus, setFetchedAtcomResTaskStatus] = React.useState<
    TypeOfAtcomResTaskStatus | undefined
  >(undefined);
  const [fetchedAtcomResTaskList, setFetchedAtcomResTaskList] = React.useState<TFF.InvoluntaryTask[] | undefined>(
    undefined,
  );
  const [tasksToUpdate, setTasksToUpdate] = React.useState<UpdateTaskRequest[] | undefined>(undefined);
  const { showLoading, closeLoading } = useContext(LoadingContext);

  const changeStateOfSelected = (
    decision: TFF.TaskDecision,
    status: TFF.TaskStatus,
    selectedTasks: Partial<TFF.InvoluntaryTask>[],
  ): void => {
    const tasksToUpdate: UpdateTaskRequest[] | undefined = selectedTasks?.map<UpdateTaskRequest>(ndcTaskToUpdate => {
      const updateTaskRequest: UpdateTaskRequest = {
        ndcTaskToUpdate: { ...ndcTaskToUpdate, decision, status },
        updateRemark: `Changes decision to ${decision} by TFF-ATCOMRES-INVOLUNTARY-CHANGES`,
        updatedBy: keycloak?.profile?.username!,
      };
      return updateTaskRequest;
    });

    setTasksToUpdate(tasksToUpdate);
  };

  useEffect(() => {
    if (!tasksToUpdate) {
      return;
    }
    const updateOnBackend = async (): Promise<void> => {
      try {
        setFetchedAtcomResTaskStatus('UPDATING');
        const modifiedTaskList = _.cloneDeep(fetchedAtcomResTaskList);
        showLoading(`Update ${tasksToUpdate.length} Tasks for AtcomRes`);
        for (let updateTaskRequest of tasksToUpdate) {
          const response = await apigeeEndpoint.post<TFF.InvoluntaryTask>(`/ndc/task`, updateTaskRequest);
          const index = modifiedTaskList?.findIndex(
            (task: TFF.InvoluntaryTask) =>
              task.recordLocator === updateTaskRequest.ndcTaskToUpdate.recordLocator &&
              task.receivedAt === updateTaskRequest.ndcTaskToUpdate.receivedAt,
          ) ?? -1;
          if (response.data && index !== -1) {
            modifiedTaskList?.splice(index, 1, response.data);
          }
        }
        setFetchedAtcomResTaskList(modifiedTaskList);

        setFetchedAtcomResTaskStatus('UPDATED');
      } catch (error) {
        setFetchedAtcomResTaskStatus('FAILURE');
      } finally {
        closeLoading();
        setTasksToUpdate(undefined);
      }
    };

    if (tasksToUpdate && tasksToUpdate.length > 0) {
      void updateOnBackend();
    }
  }, [tasksToUpdate]);

  return {
    fetchedAtcomResTaskList: fetchedAtcomResTaskList,
    fetchedAtcomResTaskStatus,
    changeStateOfSelected,
  };
};
